<script setup lang="ts">
import { adminProjectsService } from '@/services/admin/admin-projects-service.ts';
import { PhGear, PhTrash } from '@phosphor-icons/vue';
import { models } from 'cesg-shared';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AdminUserModal from './AdminUserModal.vue';

const { t } = useI18n();

const props = defineProps<{
  projectId: string | null;
  fortnoxId: string | null;
  isArchived: boolean | null;
}>();

const users = ref<models.ProjectUsersResponse[]>([]);
const selectedUser = ref<models.ProjectUsersResponse | null>(null);
const showSettingsModal = ref(false);
const showAddUserModal = ref(false);

function openSettings(user: models.ProjectUsersResponse) {
  selectedUser.value = user;
  showSettingsModal.value = true;
}

function openAddUser() {
  showAddUserModal.value = true;
}

function onAddUserModalClose() {
  showAddUserModal.value = false;
  loadUsers();
}

function onSettingsModalClose() {
  showSettingsModal.value = false;
  loadUsers();
}

async function removeUser(user: models.ProjectUsersResponse) {
  const confirmRemove = window.confirm(
    t('adminProjects.projectUser.confirmRemoveUser'),
  );
  if (confirmRemove) {
    await adminProjectsService.deletePermissionForUserInProject(
      props.projectId ?? '',
      user.id,
    );
    await loadUsers();
  }
}

const loadUsers = async () => {
  if (!props.projectId || !props.fortnoxId) {
    users.value = [];
    return;
  }

  try {
    users.value = await adminProjectsService.getAllUsersForProject(
      props.projectId,
    );
  } catch (error) {
    console.error(
      t('adminProjects.projectUser.failedToLoadProjectUser'),
      error,
    );
    users.value = [];
  }
};

onMounted(() => {
  loadUsers();
});

watch(
  () => props.fortnoxId,
  (newId) => {
    if (newId != null) loadUsers();
  },
  { immediate: true },
);
</script>

<template>
  <div class="title-container">
    <h3 class="title-left">{{ t('adminProjects.projectUser.title') }}</h3>

    <button
      v-if="!props.isArchived"
      class="button add-user-button"
      @click="openAddUser"
    >
      {{ t('adminProjects.projectUser.addUser') }}
    </button>
  </div>

  <table class="table is-striped is-fullwidth">
    <thead>
      <tr>
        <th>{{ t('adminProjects.projectUser.userName') }}</th>
        <th>{{ t('adminProjects.projectUser.userEmail') }}</th>
        <th>{{ t('adminProjects.projectUser.userPhoneNumber') }}</th>
        <th>{{ t('adminProjects.projectUser.userRole') }}</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="user in users" :key="user.id">
        <td>{{ user.name }}</td>
        <td>{{ user.email }}</td>
        <td>{{ user.phone }}</td>
        <td>{{ user.role }}</td>
        <td>
          <button class="button is-small is-white" @click="openSettings(user)">
            <PhGear size="16" weight="bold" />
          </button>
          <button class="button is-small is-white" @click="removeUser(user)">
            <PhTrash size="16" weight="bold" />
          </button>
        </td>
      </tr>
    </tbody>
  </table>

  <AdminUserModal
    v-if="showSettingsModal"
    :projectId="props.projectId ?? ''"
    :user="selectedUser"
    @close="onSettingsModalClose"
  />

  <AdminUserModal
    v-if="showAddUserModal"
    :projectId="props.projectId ?? ''"
    @close="onAddUserModalClose"
  />
</template>

<style scoped lang="scss">
.title-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.title-left {
  margin: 0;
  font-size: 1.2rem;
  font-weight: bold;
  line-height: normal;
}

.input-small {
  flex: 1;
  padding: 0.45rem 0.6rem;
  border-radius: 6px;
  border: 1px solid #c0d4f7;
  font-size: 1rem;
  box-sizing: border-box;
  height: 40px;
  margin-top: 0;
}

.add-user-button {
  background-color: var(--admin-projects-add-user);
  color: rgba(0, 0, 0, 0.7);
  border: 1px solid #c0f7c0;
  font-weight: 600;
}

.add-user-button:hover {
  background-color: var(--admin-projects-add-user-hover);
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  margin-bottom: 1rem;
}

th,
td {
  padding: 0.5rem;
  text-align: left;
}

th:last-child,
td:last-child {
  width: 1%;
  white-space: nowrap;
}

th {
  background-color: var(--admin-projects-table-head-users);
}

button {
  cursor: pointer;
}
</style>
